import { Container, Text } from 'pixi.js';
import type { CurrencyFormat, ResolvedTheme } from '../types';
import type { AudioManager } from '../audio/audio';
import { formatMoney } from '../util/format';
import { Easings, type Timeline } from './tween';

/** Big centred win counter shown under/over the reels. */
export class RollupText extends Container {
  private theme: ResolvedTheme;
  private readonly currency: CurrencyFormat;
  private readonly audio: AudioManager;
  private readonly text: Text;
  private fontSize = 48;

  constructor(o: { theme: ResolvedTheme; currency: CurrencyFormat; audio: AudioManager }) {
    super();
    this.theme = o.theme;
    this.currency = o.currency;
    this.audio = o.audio;
    this.text = new Text({ text: '', style: this.style(0xffffff) });
    this.text.anchor.set(0.5);
    this.addChild(this.text);
    this.visible = false;
    this.eventMode = 'none';
  }

  private style(color: number) {
    return {
      fontFamily: (this.theme.fonts.mono ?? this.theme.fonts.display).family,
      fontSize: this.fontSize,
      fontWeight: '900' as const,
      fill: color,
      stroke: { color: 0x000000, width: Math.max(4, this.fontSize * 0.12) },
      dropShadow: { alpha: 0.6, blur: 6, distance: 3, color: 0x000000 },
      align: 'center' as const,
    };
  }

  setFontSize(px: number): void {
    this.fontSize = px;
    this.text.style = this.style(Number(this.text.style.fill) || 0xffffff);
  }

  /** Count from 0 to `amount`; plays the rollup loop and the end sting. Resolves when done (or skipped). */
  async rollTo(amount: number, durationMs: number, timeline: Timeline, color: number): Promise<void> {
    this.text.style = this.style(color);
    this.visible = true;
    this.text.text = formatMoney(0, this.currency);
    const loop = durationMs > 150 ? this.audio.play('rollup') : null;
    await timeline.tween({
      duration: Math.max(0, durationMs),
      easing: Easings.quadOut,
      update: (t) => {
        this.text.text = formatMoney(amount * t, this.currency);
        this.text.scale.set(1 + 0.08 * Math.sin(t * Math.PI));
      },
    });
    this.text.text = formatMoney(amount, this.currency);
    this.text.scale.set(1);
    loop?.stop();
    if (durationMs > 150) this.audio.play('rollupEnd');
  }

  set(amount: number | null): void {
    if (amount == null) {
      this.visible = false;
      return;
    }
    this.text.text = formatMoney(amount, this.currency);
    this.text.scale.set(1);
    this.visible = true;
  }

  setTheme(theme: ResolvedTheme): void {
    this.theme = theme;
    this.text.style = this.style(Number(this.text.style.fill) || 0xffffff);
  }
}
